import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth, api } from '../context/AuthContext';
import { RenewalConsent } from '../components/RenewalConsent';

/**
 * Public external checkout page for the iOS app (US-only, post-2025 anti-steering).
 * Kept intentionally plain — no scare language, no upsell tricks. Just:
 *   1. Sign in (or sign up if new)
 *   2. Click "Continue to secure checkout" → Stripe Checkout ($10 + $100/mo, 7-day trial)
 */
export default function Subscribe() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [tierPreview, setTierPreview] = useState(null);
  const [consent, setConsent] = useState(false);

  // If already subscribed, send them to the full billing dashboard.
  useEffect(() => {
    if (user && ['trialing', 'active'].includes(user.subscription_status)) {
      navigate('/billing', { replace: true });
    }
  }, [user, navigate]);

  // If signed in, peek at their referral tier so we can show the discount hint.
  useEffect(() => {
    if (!user) { setTierPreview(null); return; }
    (async () => {
      try {
        const { data } = await api.get('/referrals/summary');
        setTierPreview(data.next_redemption || null);
      } catch { /* not critical */ }
    })();
  }, [user]);

  const startCheckout = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/checkout', { renewal_consent: true });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast({
        title: 'Checkout unavailable',
        description: err?.response?.data?.detail || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  }, [toast]);

  return (
    <div className="min-h-screen bg-brand-cream/40 flex items-center justify-center p-6" data-testid="subscribe-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Subscribe</span>
          <h1 className="mt-2 font-serif text-3xl text-brand-green">LISTING AiGENT Realtor</h1>
          <p className="mt-2 text-sm text-brand-green/70">
            $10 activation, 7-day trial, then $100/month.
          </p>
        </div>

        <ul className="mt-6 space-y-2 text-sm text-brand-green/80">
          {['Unlimited listings',
            'AI Call Intake — dedicated phone assistant',
            'Buyer-match blasts + Open House RSVPs',
            'Google Calendar sync',
            'Cancel anytime during the trial'].map((f) => (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        {tierPreview && (
          <div data-testid="subscribe-tier-hint"
               className="mt-5 text-xs text-brand-green bg-brand-gold/15 border border-brand-gold/30 rounded-lg p-3">
            🎉 Your next checkout applies <strong>{tierPreview.label}</strong> from
            {' '}{tierPreview.consumes} referred realtor{tierPreview.consumes === 1 ? '' : 's'}.
          </div>
        )}

        <div className="mt-6">
          {!user ? (
            <div className="space-y-3">
              <Button asChild className="w-full" data-testid="subscribe-signin-btn">
                <Link to={`/login?next=${encodeURIComponent('/subscribe')}`}>Sign in and continue</Link>
              </Button>
              <Button asChild variant="outline" className="w-full" data-testid="subscribe-signup-btn">
                <Link to={`/signup?next=${encodeURIComponent('/subscribe')}`}>Create an account</Link>
              </Button>
            </div>
          ) : (
            <>
              <RenewalConsent checked={consent} onChange={setConsent} />
              <Button className="w-full mt-4" onClick={startCheckout} disabled={busy || !consent}
                      data-testid="subscribe-checkout-btn">
                {busy ? 'Redirecting to secure checkout…' : 'Continue to secure checkout'}
              </Button>
            </>
          )}
        </div>

        <p className="mt-6 text-[10px] text-center text-brand-green/50 leading-relaxed">
          Payments processed by Stripe. By continuing you agree to our{' '}
          <Link to="/terms" className="underline">Terms</Link> and{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
